<template>
	<div
		v-if="navEnabled"
		ref="feedNav"
		class="feed-nav"
		:class="{ 'has-no-header-sponsors': !shouldApplyConditionalStyles }"
		:style="conditionalStyles"
	>
		<div
			ref="navInner"
			class="feed-nav__inner"
			@mouseleave="onMouseLeave"
		>
			<div
				v-for="(item, index) in navItems"
				:key="item.label"
				ref="navItems"
				class="feed-nav__item"
				@mouseenter="onItemHover(index)"
				@touchstart="onItemTouch(index)"
			>
				<a
					:href="item.href"
					class="feed-nav__item-link"
					:class="{ 'is-active': item.active }"
					:aria-current="item.active ? 'page' : null"
					@click="onItemClick(index)"
				>
					<Icon
						:icon-name="item.icon"
						class="feed-nav__item-icon"
						size="lg"
					/>
					<span class="feed-nav__item-text">{{ item.label }}</span>
				</a>
			</div>
			<div
				ref="activeIndicator"
				class="feed-nav__active-indicator"
				:style="indicatorStyle"
			/>
		</div>
	</div>
</template>

<script>
import laroute from '@laroute';
import { mapGetters } from 'vuex';
import Icon from '@shared/components/Icon';
import { throttle } from '@shared/utils/throttle';

export default {
	name:       'FeedNavigationDesktop',
	components: { Icon },
	data() {
		return {
			activeIndex:                -1,
			clickedIndex:               -1,
			indicatorStyle:             {},
			isInitialLoad:              true,
			conditionalStyles:          {},
			shouldApplyConditionalFlag: false,
		};
	},
	computed: {
		...mapGetters({
			userInitialized:  'user/isInitialized',
			profile:          'user/profile',
			profileUrl:       'user/profileUrl',
			isMobileViewport: 'device/isMobileViewport',
		}),
		navItems() {
			const items = [
				{
					label:  this.$I18n.trans('commons.arena_profile'),
					icon:   'stadium',
					href:   '/',
					active: this.clickedIndex === 0 || (this.clickedIndex === -1 && window.location.pathname === '/'),
				},
			];

			if (this.$arenaConfig.enableVideoFeed) {
				items.push({
					label:  this.$I18n.trans('arena.feed.label'),
					icon:   'video-gallery',
					href:   `${laroute.route('arena.feed')}`,
					active: this.clickedIndex === 1 || (this.clickedIndex === -1 && window.location.pathname.startsWith('/feed')),
				});
			}

			if (this.$arenaConfig.enableMomentsFeed && this.userInitialized && this.profile) {
				const momentsIndex = this.$arenaConfig.enableVideoFeed ? 2 : 1;
				items.push({
					label:  this.$I18n.trans('event.multiformat_editor.title'),
					icon:   'formatconverter',
					href:   `${laroute.route('arena.moments')}`,
					active: this.clickedIndex === momentsIndex || (this.clickedIndex === -1 && window.location.pathname.startsWith('/moments')),
				});
			}
			return items;
		},
		navEnabled() {
			return this.navItems.length > 1 && !this.isMobileViewport;
		},
		shouldApplyConditionalStyles() {
			return this.shouldApplyConditionalFlag;
		},
	},
	watch: {
		navItems: {
			handler() {
				this.$nextTick(() => {
					this.updateActiveIndex();
					this.isInitialLoad = true;
					setTimeout(() => {
						if (this.navEnabled) {
							this.updateIndicatorPosition();
						}
					}, 50);
				});
			},
			deep: true,
		},
		navEnabled: {
			handler(newVal) {
				if (newVal) {
					this.$nextTick(() => {
						this.updateIndicatorPosition();
						// Now that nav is enabled and DOM should be rendered, check conditional styles
						this.checkConditionalStylesCondition();
						this.updateConditionalStyles();
					});
				} else {
					this.conditionalStyles = {};
				}
			},
		},
		shouldApplyConditionalFlag: {
			handler(newVal) {
				if (newVal) {
					this.$nextTick(() => {
						// Ensure DOM is ready before updating conditional styles
						if (this.$refs.feedNav) {
							this.updateConditionalStyles();
						} else {
							// Retry after a short delay if ref not available yet
							setTimeout(() => {
								if (this.$refs.feedNav) {
									this.updateConditionalStyles();
								}
							}, 10);
						}
					});
				} else {
					this.conditionalStyles = {};
				}
			},
		},
	},
	mounted() {
		this.$nextTick(() => {
			this.clickedIndex = -1;
			this.updateActiveIndex();
			setTimeout(() => {
				if (this.navEnabled) {
					this.updateIndicatorPosition();
				}
			}, 50);
		});
		window.addEventListener('resize', this.throttledHandleResize);
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.throttledHandleResize);
	},
	methods: {
		updateActiveIndex() {
			this.activeIndex = this.navItems.findIndex(item => item.active);
		},
		updateIndicatorPosition(targetIndex = null) {
			if (!this.navEnabled) {
				return;
			}

			const index = targetIndex !== null ? targetIndex : this.activeIndex;
			if (index === -1 || !this.$refs.navItems || !this.$refs.navItems[index]) {
				if (this.isInitialLoad) {
					this.isInitialLoad = false;
				}
				this.indicatorStyle = { transform: 'translateX(-100%)', width: '0px', transition: 'none' };
				return;
			}

			const itemElement = this.$refs.navItems[index];
			const navInner = this.$refs.navInner;

			if (!itemElement || !navInner) {
				if (this.isInitialLoad) {
					setTimeout(() => this.updateIndicatorPosition(targetIndex), 50);
				}
				return;
			}

			const itemRect = itemElement.getBoundingClientRect();
			const navRect = navInner.getBoundingClientRect();

			const left = itemRect.left - navRect.left;
			const width = itemRect.width;

			let useTransition = false;
			if (this.isInitialLoad) {
				useTransition = false;
				this.isInitialLoad = false;
			} else if (targetIndex !== null) {
				useTransition = true;
			} else {
				useTransition = true;
			}

			this.indicatorStyle = {
				transform:  `translateX(${left}px)`,
				width:      `${width}px`,
				transition: useTransition ? 'transform 0.3s ease, width 0.3s ease' : 'none',
			};
		},
		onItemHover(index) {
			this.updateIndicatorPosition(index);
		},
		onMouseLeave() {
			this.updateIndicatorPosition(null);
		},
		onItemTouch(index) {
			this.activeIndex = index;
			this.updateIndicatorPosition();
		},
		onItemClick(index) {
			this.clickedIndex = index;
			this.updateActiveIndex();
		},
		handleResize() {
			this.$nextTick(() => {
				if (this.navEnabled) {
					this.updateIndicatorPosition();
					this.checkConditionalStylesCondition();
					this.updateConditionalStyles();
				}
			});
		},
		throttledHandleResize: throttle(function() {
			this.handleResize();
		}, 150),
		updateConditionalStyles() {
			if (!this.shouldApplyConditionalStyles || !this.$refs.feedNav) {
				this.conditionalStyles = {};
				return;
			}

			const previousSibling = this.$refs.feedNav.previousElementSibling;
			if (!previousSibling) {
				this.conditionalStyles = {};
				return;
			}

			// Get the computed margin-bottom of the previous sibling
			const computedStyle = window.getComputedStyle(previousSibling);
			const marginBottom = computedStyle.marginBottom;

			// Apply negative margin and translateY
			this.conditionalStyles = {
				marginTop: `-${marginBottom}`,
				transform: 'translateY(-50%)',
			};
		},
		checkConditionalStylesCondition() {
			if (!this.$refs.feedNav) {
				return;
			}

			const previousSibling = this.$refs.feedNav.previousElementSibling;
			if (!previousSibling) {
				this.shouldApplyConditionalFlag = false;
				return;
			}

			// Check if previous sibling has a child with .js-header-sponsors
			const hasHeaderSponsorsChild = previousSibling.querySelector('.js-header-sponsors') !== null;

			// Apply conditional styles only if previous sibling does NOT have .js-header-sponsors child
			this.shouldApplyConditionalFlag = !hasHeaderSponsorsChild;
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@sass/variables';

.feed-nav {
	--hover-color: var(--color-white);
	--active-color: var(--color-white);
	--bg-color: var(--color-grey-900);
	--indicator-bg-color: var(--color-grey-800);
	--width: 140px;
	--height: 46px;
	--font-size: 16px;

	@include z-index(arena-sticky-feed-nav);
	display: flex;
	justify-content: center;
	position: sticky;
	top: 110px;
	margin-bottom: $sp7;
	width: 100%;

	&.has-no-header-sponsors {
		top: 85px;
	}

	body.-t-light & { // stylelint-disable-line
		--active-color: var(--color-black);
		--hover-color: var(--color-black);
		--bg-color: var(--color-grey-950);
		--indicator-bg-color: var(--color-grey-850);
	}

	@include size(lg-up) {
		--width: 162px;
		--height: 52px;
		--font-size: 18px;
	}
}

.feed-nav__inner {
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: var(--bg-color);
	border-radius: $border-radius--full;
	overflow: hidden;
	position: relative;
}

.feed-nav__item {
	display: flex;
	position: relative;
	z-index: 2;
}

.feed-nav__item-link {
	display: flex;
	gap: $sp3-5;
	width: var(--width);
	height: var(--height);
	align-items: center;
	justify-content: center;
	background-color: transparent;
	border-radius: $border-radius--full;
	color: var(--color-grey-300);
	transition: color $trans-time-fast;
	font-weight: 500; // stylelint-disable-line
	font-size: var(--font-size); // stylelint-disable-line
	position: relative;
	z-index: 3; // stylelint-disable-line

	&:hover {
		color: var(--hover-color);
	}

	&.is-active {
		color: var(--active-color);
	}
}

.feed-nav__active-indicator {
	position: absolute;
	top: 0;
	left: 0;
	height: var(--height);
	background-color: var(--indicator-bg-color);
	border-radius: $border-radius--full;
	z-index: 1;
	transition: transform 0.3s ease, width 0.3s ease;
	pointer-events: none;
}
</style>
